import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import {
  Link,
  Switch,
  Route,
  useHistory,
  useParams,
  useLocation,
  useRouteMatch
} from 'react-router-dom';
import { css, StyleSheet } from 'aphrodite/no-important';
import ProviderPracticeGrid from './provider-practice-grid';
import { addAppAlert } from '../../actions';
import { BASE_URL, DRAWER_BREAK_POINT, debugLogToConsole } from '../../utils';
import AboutUser from './about-user';
import RevenueCycleManagement from '../revenue-cycle-management';
import { gray, blue, salmon, theme } from '../../theme';
import ProfileAccessManager from '../profile-access-manager';
import NoView from '../ no-view';

const drawerWidth = window.innerWidth <= DRAWER_BREAK_POINT ? window.innerWidth : 320;

const NAVBAR_HEIGHT = 70;

const noAccessIcon = (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="22"
    height="19"
    fill="none"
  >
    <path
      fill="#96C8FF"
      fillRule="evenodd"
      d="m3.281 3.55-2.28-2.28L2.27 0l17.738 17.73L18.74 19l-2.932-2.92-.42-.42c-1.35.54-2.831.84-4.382.84C6.003 16.5 1.731 13.39 0 9c.78-1.98 2.081-3.7 3.742-4.99l-.46-.46ZM16.007 9c0-2.76-2.24-5-5.002-5-.65 0-1.26.13-1.83.36L7.012 2.2c1.24-.45 2.581-.7 3.982-.7C15.997 1.5 20.269 4.61 22 9c-.73 1.86-1.92 3.49-3.432 4.75l-2.92-2.92c.23-.57.36-1.18.36-1.83ZM6.533 6.8l1.55 1.55c-.05.21-.08.43-.08.65 0 1.66 1.341 3 3.002 3 .22 0 .44-.03.65-.08l1.551 1.55c-.67.33-1.41.53-2.201.53a5.003 5.003 0 0 1-5.002-5c0-.79.2-1.53.53-2.2Zm7.463 2.37-3.151-3.15.17-.01c1.66 0 3.001 1.34 3.001 3l-.02.16Z"
      clipRule="evenodd"
    />
  </svg>
);

const readOnlyIcon = (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="15"
    fill="none"
  >
    <path
      fill="#96C8FF"
      fillRule="evenodd"
      d="M10 0C5.455 0 1.573 3.11 0 7.5 1.573 11.89 5.455 15 10 15c4.546 0 8.427-3.11 10-7.5C18.427 3.11 14.546 0 10 0Zm0 12.5c-2.51 0-4.545-2.24-4.545-5s2.036-5 4.545-5c2.51 0 4.546 2.24 4.546 5s-2.037 5-4.546 5Zm-2.727-5c0-1.66 1.218-3 2.727-3 1.51 0 2.727 1.34 2.727 3s-1.218 3-2.727 3c-1.51 0-2.727-1.34-2.727-3Z"
      clipRule="evenodd"
    />
  </svg>
);

const readWriteIcon = (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="18"
    height="18"
    fill="none"
  >
    <path
      fill="#96C8FF"
      fillRule="evenodd"
      d="M17.707 2.632c.39.39.39 1.02 0 1.41l-1.83 1.83-3.749-3.75 1.83-1.83a.996.996 0 0 1 1.41 0l2.34 2.34ZM0 18v-3.75L11.059 3.193l3.749 3.75L3.749 18H0Z"
      clipRule="evenodd"
    />
  </svg>
);

const drawerTabs = {
  about: 'About Profile',
  scheduling: 'Scheduling',
  revenueCycle: 'Revenue Cycle Management',
  credentialing: 'Credentialing',
  practices: 'Practices',
  facilities: 'Facilities',
  companies: 'Companies'
};

const ProviderFacilities = (props) => {
  return (
    <h1>Provider Facilities</h1>
  );
};

const ProviderCompanies = (props) => {
  return (
    <h1>Provider Companies</h1>
  );
};

const Placeholder = props => <h1>{props.text}</h1>;

// Main Component --> ProviderDashboard --> 🥼
const ProviderDashboard = (props) => {
  const [isMobileLayout, setMobileLayout] = useState(false);
  const [isDrawerOpen, toggleDrawer] = useState(true);
  const [activeComponent, setActiveComponent] = useState(drawerTabs.about);
  const [activeProvider, setActiveProvider] = useState({});
  const [windowHeight, setWindowHeight] = useState(window.innerHeight);
  const [isProfileAdmin, setIsProfileAdmin] = useState(false);
  const [profileAccess, setProfileAccess] = useState({
    profile: 0,
    caseLedger: 0,
    paymentsLedger: 0,
    credentialing: 0,
    payerContract: 0,
    practiceAffiliations: 0,
    facilityAffiliations: 0,
    companyAffiliations: 0,
    profileAccessManager: 0
  });

  // React Router Hooks
  const history = useHistory();
  const location = useLocation();
  const { path, url } = useRouteMatch();
  const { providerId } = useParams();
  const { pathname } = location;

  // Split panel theme
  const {
    drawerBackground,
    drawerBorderColor,
    drawerTextColor,
    drawerFontSize,
    drawerFontWeight,
    // Drawer Tabs
    tabBackground,
    tabActiveBackground,
    tabHoverBackground,
    tabIndicatorBackground,
    tabIndicatorHoverBackground,
    tabIndicatorActiveBackground,
    tabTextColor,
    tabHoverTextColor,
    tabActiveTextColor,
    tabFontSize,
    tabFontWeight,
    groupSeparators,
    headerBackground,
    headerTextColor,
    headerFontSize,
    headerFontWeight,
    groupHeaderTextColor,
    groupHeaderTextOpacity,
    groupHeaderFontSize,
    groupHeaderFontWeight,
    groupTabIndent
  } = theme.splitPanelDrawer;

  const loadUserData = () => {
    const reqHeaders = new Headers();
    const token = localStorage.getItem('token');
    reqHeaders.append('Authorization', `Bearer ${token}`);
    const url = new URL(`${BASE_URL}/users/getProviderProfile`);
    const params = { providerId };
    url.search = new URLSearchParams(params).toString();

    fetch(url, { method: 'GET', headers: reqHeaders })
      .then(response => response.text())
      .then(result => {
        const reqData = JSON.parse(result);
        setProfileAccess(reqData.access);
        setIsProfileAdmin(reqData.isAdmin || false);
        setActiveProvider(reqData.user);
      })
      .catch(error => console.log('error', error));
  };

  const addSurgicalProcedure = (proc) => {
    const formData = new FormData();
    formData.append('userId', activeProvider._id);
    formData.append('procedureCode', proc.code);

    const requestOptions = {
      method: 'POST',
      body: formData,
      redirect: 'follow'
    };

    fetch(`${BASE_URL}/users/addSurgicalProcedureForUser`, requestOptions)
      .then(response => response.text())
      .then(result => {
        const res = JSON.parse(result);
        console.log(res);
      })
      .catch(error => console.log('error', error));
  };

  const removeSurgicalProcedure = (proc) => {
    const formData = new FormData();
    formData.append('userId', activeProvider._id);
    formData.append('procedureCode', proc.code);

    const requestOptions = {
      method: 'POST',
      body: formData,
      redirect: 'follow'
    };

    fetch(`${BASE_URL}/users/removeSurgicalProcedureForUser`, requestOptions)
      .then(response => response.text())
      .then(result => {
        const res = JSON.parse(result);
        console.log(res);
      })
      .catch(error => console.log('error', error));
  };

  const handleWindowResize = () => {
    const isMobile = window.innerWidth <= 768;
    // console.log('isMobile', isMobile);
    toggleDrawer(!isMobile);
    setMobileLayout(isMobile);
  };

  const handleOrientationChanged = () => {
    console.log('Orientation changed!');
    setWindowHeight(windowHeight.innerHeight);
  };

  // Load user data
  useEffect(() => {
    window.addEventListener('orientationchange', handleOrientationChanged);
    loadUserData();
    return () => {
      window.removeEventListener('orientationchange', handleOrientationChanged);
    };
  }, []);

  // Window resize event effect
  useEffect(() => {
    window.addEventListener('resize', handleWindowResize);
    handleWindowResize();
    return () => {
      window.removeEventListener('resize', handleWindowResize);
    };
  }, []);

  // Refs
  // const userIcon = React.createRef();

  // ------------------------------ Styles ------------------------------
  const styles = StyleSheet.create({
    dashboardHeader: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      height: '32px',
      padding: '0 8px',
      background: gray[500],
      color: gray[300]
    },
    dashboardHeaderIcon: {
      fontSize: '18px',
      color: salmon[500],
      cursor: 'pointer'
    },
    splitPanel: {
      display: 'flex',
      flexFlow: 'row nowrap',
      background: gray[800],
      height: 'calc(100% - 32px)',
      width: '100%',
      overflow: 'hidden',
      fontFamily: 'Lato'
    },
    drawer: {
      // display: isProfileAdmin ? '' : 'none',
      position: 'relative',
      height: '100%',
      transition: '0.3s',
      padding: isDrawerOpen ? '8px' : '0',
      overflowY: 'auto',
      zIndex: '1900',
      borderRight: `1px solid ${gray[700]}`,
      background: gray[800]
    },
    openDrawer: {
      left: '0',
      minWidth: `${drawerWidth}px`,
      width: `${drawerWidth}px`
    },
    hideDrawer: {
      transition: '0.3s',
      width: '0px',
      minWidth: '0px',
      left: `calc(-1 * ${drawerWidth}px)`
    },
    drawerHeader: {
      height: '40px',
      fontSize: '16px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'left',
      cursor: 'pointer',
      background: headerBackground,
      color: headerTextColor,
      borderBottom: `0.5px solid ${drawerBorderColor}`,
      ':hover:not(.activeitem)': {
        background: tabHoverBackground,
        color: tabHoverTextColor
      }
    },
    drawerHeaderActive: {
      color: tabActiveTextColor,
      background: tabActiveBackground
    },
    drawerHeaderIconContainer: {
      height: '32px',
      // width: '32px',
      borderRadius: '4px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      margin: '0 4px',
      cursor: 'pointer'
    },
    drawerHeaderIcon: {
      fontSize: '24px',
      cursor: 'pointer'
    },
    drawerHeaderTextContainer: {
      textAlign: 'left',
      lineHeight: '16px',
      cursor: 'pointer'
    },
    drawerHeaderText: {
      textAlign: 'center',
      fontSize: '14px',
      cursor: 'pointer',
      paddingLeft: '4px',
      fontWeight: 'bold'
    },
    drawerHeaderTextSub: {
      fontSize: '12px',
      cursor: 'pointer'
    },
    drawerTabsContainer: {
      height: 'calc(100% - 40px)',
      // height: `${window.innerHeight - 95}px`,
      overflowY: 'auto'
    },
    drawerAccordian: {
      position: 'relative',
      display: 'block',
      transition: '0.5s ease',
      maxHeight: '2000px'
    },
    drawerFile: {
      display: 'flex',
      alignItems: 'center',
      paddingLeft: '8px',
      color: blue[200],
      height: '40px'
    },
    drawerMenuItem: {
      position: 'relative',
      marginTop: '1px',
      background: blue[800],
      color: tabTextColor,
      fill: tabTextColor,
      borderLeft: `1px solid ${tabIndicatorBackground}`,
      height: '40px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingLeft: groupTabIndent,
      paddingRight: '8px',
      ':hover:not(.activeitem):not(.disabled)': {
        background: tabHoverBackground,
        color: tabHoverTextColor,
        fill: tabHoverTextColor,
        borderLeft: `1px solid ${tabIndicatorHoverBackground}`
      }
    },
    drawerMenuItemIcon: {
      height: '24px',
      width: '42px',
      minWidth: '24px',
      marginRight: '8px',
      display: 'flex',
      flexFlow: 'row nowrap',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '24px',
      fill: 'inherit'
    },
    drawerMenuItemActive: {
      // fill: blue[500],
      // color: blue[500],
      // background: blue[900]
      fill: tabActiveTextColor,
      color: tabActiveTextColor,
      background: tabActiveBackground
    },
    drawerMenuItemText: {
      width: '100%',
      fontSize: '1rem',
      lineHeight: '14px'
    },
    linkStyles: {
      display: 'flex',
      alignItems: 'center',
      color: 'inherit',
      ':hover': {
        color: 'inherit'
      }
    },
    selectedLink: {
      color: blue[500]
    },
    menuItem: {
      position: 'relative',
      left: '-3px',
      color: gray[300]
    },
    detail: {
      transition: '0s',
      height: `calc(100vh - ${NAVBAR_HEIGHT}px)`,
      left: '0'
    },
    detailExapnd: {
      transition: '0.2s',
      left: 'calc(-1 * 320px)',
      width: '100vw'
    },
    detailHeader: {
      height: '40px',
      background: gray[900],
      display: 'flex',
      alignItems: 'center',
      borderBottom: `1px solid ${gray[500]}`,
      padding: '0 8px'
    }
  });

  const handleOnClickMiniUserCard = () => {
    // if (isMobileLayout) {
    //   toggleDrawer(false);
    // }
    // setActiveComponent(drawerTabs.about);
    history.push(`${url}/profile`);
    if (isMobileLayout) {
      toggleDrawer(false);
    }
  };

  const handelOnShowAppAlert = (alert) => {
    props.addAppAlert(alert);
  };

  const handleReloadUserData = () => {
    loadUserData();
  };

  let activeText = '';
  switch (activeComponent) {
    case drawerTabs.scheduling:
      activeText = drawerTabs.scheduling;
      break;
    case drawerTabs.revenueCycle:
      activeText = drawerTabs.revenueCycle;
      break;
    case drawerTabs.credentialing:
      activeText = drawerTabs.credentialing;
      break;
    case drawerTabs.practices:
      activeText = drawerTabs.practices;
      break;
    case drawerTabs.facilities:
      activeText = drawerTabs.facilities;
      break;
    case drawerTabs.companies:
      activeText = drawerTabs.companies;
      break;
    case drawerTabs.about:
    default:
      activeText = drawerTabs.about;
  }

  console.log(activeProvider);
  // ------------------------------ Component ------------------------------
  return (
    <div style={{ height: 'calc(100% - 40px)' }}>
      {/* Dashboard Header */}
      <div className={css(styles.dashboardHeader)}>
        <div>User Profile</div>
        <div
          className={css(styles.dashboardHeaderIcon)}
          onClick={() => {
            if (props.usr.isMasterAdmin) {
              const appLocs = JSON.parse(localStorage.getItem('appLocs')) || [];
              if (appLocs.length > 0) {
                const newPath = appLocs.pop();
                localStorage.setItem('appLocs', JSON.stringify(appLocs));
                history.push(newPath);
              } else {
                history.push('/admin/users');
              }
            } else {
              const lastBookmark = localStorage.getItem('lastBookmark') || '/user/profile/about';
              history.push(lastBookmark);
              localStorage.setItem('lastBookmark', '');
            }
          }}
        >
          <i className='fa fa-times-circle' />
        </div>
      </div>
      {/* Split Panel */}
      <div className={css(styles.splitPanel)}>
        {/* Panel Drawer */}
        <div className={css(styles.drawer, isDrawerOpen ? styles.openDrawer : styles.hideDrawer)}>
          {/* Drawer header (Mini user card) */}
          <div
            className={`noselect
              ${(pathname.indexOf('/profile') > -1) ? 'activeitem' : ''}
              ${css(styles.drawerMenuItem, (pathname.indexOf('/profile') > -1) && styles.drawerMenuItemActive)}`
            }
            onClick={handleOnClickMiniUserCard}
          >
            {/* Header icon */}
            <div className={css(styles.drawerHeaderIconContainer)}>
              {activeProvider && activeProvider.avatarImageUrl !== ''
                ? <img
                  src={activeProvider.avatarImageUrl}
                  alt="avatar"
                  style={{
                    width: 'auto',
                    height: '32px',
                    maxHeight: '32px',
                    borderRadius: '4px'
                  }}
                />
                : <i className={`fa fa-user-o ${css(styles.drawerHeaderIcon)}`} />
              }
              <div className='ml-1' style={{ lineHeight: '14px' }}>
                <div className={css(styles.drawerHeaderText)}>
                  {`${activeProvider.displayName ? activeProvider.displayName : 'N/A'}${activeProvider.credentials ? `,${activeProvider.credentials}` : ''}`}
                </div>
                <div className={css(styles.drawerHeaderTextSub)}>
                  {`@${activeProvider.userName ? activeProvider.userName : 'n/a'}`}
                </div>
              </div>
            </div>
            <div className='fx fx__v-center'>
              {profileAccess.profile === 6 && readWriteIcon}
              {profileAccess.profile === 4 && readOnlyIcon}
              {profileAccess.profile === 0 && noAccessIcon}
            </div>
          </div>
          {/* Drawer tabs */}
          <div className={css(styles.drawerTabsContainer)}>
            {/* My Provider Folder */}
            <div
              className={css(styles.drawerAccordian)}
              style={{ display: activeProvider.npi ? '' : 'none' }}
            >
              {/* ------ Case Ledger ------ */}
              <div
                onClick={() => {
                  if (profileAccess.caseLedger !== 0) {
                    history.push(`${url}/revenue`);
                    if (isMobileLayout) {
                      toggleDrawer(false);
                    }
                  }
                }}
                className={`noselect
                  ${profileAccess.caseLedger === 0 ? 'disabled' : ''}
                  ${(pathname.endsWith('/revenue') || pathname.endsWith('/cases')) ? 'activeitem' : ''}
                  ${css(styles.drawerMenuItem, (pathname.endsWith('/revenue') || pathname.endsWith('/cases')) &&
                  styles.drawerMenuItemActive)}`}
                style={{ cursor: profileAccess.caseLedger === 0 ? 'not-allowed' : 'pointer' }}
              >
                <Link
                  to={`${url}/revenue`}
                  onClick={() => {
                    if (profileAccess.caseLedger !== 0) {
                      if (isMobileLayout) {
                        toggleDrawer(false);
                      }
                    }
                  }}
                  className={`${profileAccess.caseLedger === 0 ? 'disabled-link' : ''} ${css(styles.linkStyles, pathname.endsWith('/revenue') && styles.selectedLink)}`}
                >
                  {/* Icon */}
                  <div className={css(styles.drawerMenuItemIcon)}>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      fill="inherit"
                    >
                      <path
                        fill="inherit"
                        fillRule="evenodd"
                        d="M16.8 5.053h4.8c1.332 0 2.4 1.124 2.4 2.526v13.895C24 22.876 22.932 24 21.6 24H2.4C1.068 24 0 22.876 0 21.474L.012 7.579c0-1.402 1.056-2.526 2.388-2.526h4.8V2.526C7.2 1.124 8.268 0 9.6 0h4.8c1.332 0 2.4 1.124 2.4 2.526v2.527Zm-7.2-.001h4.8V2.525H9.6v2.527Z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <div className={css(styles.drawerMenuItemText)}>
                    Case Ledger
                  </div>
                </Link>
                {/* Access */}
                <div className='fx fx__v-center'>
                  {profileAccess.caseLedger === 6 && readWriteIcon}
                  {profileAccess.caseLedger === 4 && readOnlyIcon}
                  {profileAccess.caseLedger === 0 && noAccessIcon}
                </div>
              </div>
              {/* ------ Payments Ledger ------ */}
              <div
                onClick={() => {
                  if (profileAccess.paymentsLedger !== 0) {
                    history.push(`${url}/payments`);
                    if (isMobileLayout) {
                      toggleDrawer(false);
                    }
                  }
                }}
                className={`noselect
                  ${profileAccess.paymentsLedger === 0 ? 'disabled' : ''}
                  ${pathname.endsWith('/payments') ? 'activeitem' : ''}
                  ${css(styles.drawerMenuItem, pathname.endsWith('/payments') &&
                  styles.drawerMenuItemActive)}`}
                style={{ cursor: profileAccess.paymentsLedger === 0 ? 'not-allowed' : 'pointer' }}
              >
                <Link
                  to={`${url}/payments`}
                  onClick={() => {
                    if (profileAccess.paymentsLedger !== 0) {
                      if (isMobileLayout) {
                        toggleDrawer(false);
                      }
                    }
                  }}
                  // className={css(styles.linkStyles, pathname.endsWith('/payments') && styles.selectedLink)}
                  className={`${profileAccess.paymentsLedger === 0 ? 'disabled-link' : ''} ${css(styles.linkStyles, pathname.endsWith('/payments') && styles.selectedLink)}`}
                >
                  {/* Icon */}
                  <div className={css(styles.drawerMenuItemIcon)}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="inherit">
                      <path fill="inherit" fillRule="evenodd" d="M23.157 3a.843.843 0 0 0-.844.844v2.015A12.007 12.007 0 0 0 18.633 2 11.94 11.94 0 0 0 12.001 0a11.955 11.955 0 0 0-8.18 3.22 11.958 11.958 0 0 0-3.787 7.873.844.844 0 0 0 .778.904C.833 12 .854 12 .875 12a.845.845 0 0 0 .842-.78A10.278 10.278 0 0 1 4.97 4.454 10.28 10.28 0 0 1 12 1.687c2.04 0 4.01.593 5.7 1.718a10.303 10.303 0 0 1 3.488 3.907h-2.344a.843.843 0 1 0 0 1.688h4.313A.843.843 0 0 0 24 8.156V3.844A.843.843 0 0 0 23.157 3Zm.029 9.002a.845.845 0 0 0-.905.778 10.278 10.278 0 0 1-3.253 6.764A10.27 10.27 0 0 1 12 22.312a10.25 10.25 0 0 1-5.7-1.718 10.303 10.303 0 0 1-3.488-3.907h2.344a.843.843 0 1 0 0-1.688H.844a.843.843 0 0 0-.844.844v4.313a.843.843 0 1 0 1.687 0V18.14A12.025 12.025 0 0 0 5.365 22c1.968 1.308 4.26 2 6.635 2 3.046 0 5.953-1.145 8.179-3.221a11.944 11.944 0 0 0 3.785-7.872.843.843 0 0 0-.778-.905ZM10.653 9.833c0 .633.487 1.04 2 1.433 1.513.394 3.133 1.04 3.133 2.934 0 1.366-1.033 2.12-2.333 2.366V18h-2v-1.446c-1.28-.274-2.373-1.094-2.453-2.554h1.466c.074.787.614 1.4 1.987 1.4 1.473 0 1.8-.733 1.8-1.193 0-.62-.334-1.207-2-1.606-1.86-.447-3.133-1.214-3.133-2.754 0-1.286 1.04-2.126 2.333-2.406V6h2v1.46c1.393.34 2.093 1.393 2.14 2.54H14.12c-.04-.834-.48-1.4-1.667-1.4-1.127 0-1.8.506-1.8 1.233Z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className={css(styles.drawerMenuItemText)}>
                    Payments Ledger
                  </div>
                </Link>
                {/* Access */}
                <div className='fx fx__v-center'>
                  {profileAccess.paymentsLedger === 6 && readWriteIcon}
                  {profileAccess.paymentsLedger === 4 && readOnlyIcon}
                  {profileAccess.paymentsLedger === 0 && noAccessIcon}
                </div>
              </div>
              {/* ------ Credentialing ------ */}
              <div
                onClick={() => {
                  if (profileAccess.credentialing !== 0) {
                    history.push(`${url}/credentialing`);
                    if (isMobileLayout) {
                      toggleDrawer(false);
                    }
                  }
                }}
                className={`noselect
                  ${profileAccess.credentialing === 0 ? 'disabled' : ''}
                  ${pathname.endsWith('/credentialing') ? 'activeitem' : ''}
                  ${css(styles.drawerMenuItem, pathname.endsWith('/credentialing') &&
                  styles.drawerMenuItemActive)}`}
                style={{ cursor: profileAccess.credentialing === 0 ? 'not-allowed' : 'pointer' }}
              >
                <Link
                  to={`${url}/credentialing`}
                  onClick={() => {
                    if (profileAccess.credentialing !== 0) {
                      if (isMobileLayout) {
                        toggleDrawer(false);
                      }
                    }
                  }}
                  // className={css(styles.linkStyles, pathname.endsWith('/credentialing') && styles.selectedLink)}
                  className={`${profileAccess.credentialing === 0 ? 'disabled-link' : ''} ${css(styles.linkStyles, pathname.endsWith('/credentialing') && styles.selectedLink)}`}
                >
                  {/* Icon */}
                  <div className={css(styles.drawerMenuItemIcon)}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="23" fill="inherit">
                      <path fill="inherit" d="M24 11.444 21.338 8.4l.371-4.025-3.938-.895L15.709 0 12 1.593 8.29 0 6.23 3.48l-3.94.884.372 4.036L0 11.444l2.662 3.043-.371 4.037 3.938.894 2.062 3.491L12 21.305l3.71 1.593 2.06-3.48 3.94-.894-.372-4.026L24 11.444Zm-4.31 1.614-.61.71.087.926.197 2.128-2.99.676-.48.807-1.08 1.833-1.941-.84-.873-.37-.862.37-1.942.84-1.08-1.822-.48-.807-2.989-.676.197-2.138.087-.928-.611-.709-1.407-1.604L4.32 9.84l.61-.71-.097-.937-.197-2.117 2.99-.676.48-.807 1.08-1.833 1.941.84.873.37.862-.37 1.942-.84 1.08 1.833.48.807 2.989.676-.197 2.128-.087.927.611.709 1.407 1.604-1.396 1.614Z" /><path fill="inherit" d="m9.916 13.364-2.53-2.542-1.615 1.625 4.145 4.157 8.008-8.03-1.615-1.625-6.393 6.415Z" />
                    </svg>
                  </div>
                  <div className={css(styles.drawerMenuItemText)}>
                    Credentialing
                  </div>
                </Link>
                {/* Access */}
                <div className='fx fx__v-center'>
                  {profileAccess.credentialing === 6 && readWriteIcon}
                  {profileAccess.credentialing === 4 && readOnlyIcon}
                  {profileAccess.credentialing === 0 && noAccessIcon}
                </div>
              </div>
              {/* ------ Insurance ------ */}
              <div
                onClick={() => {
                  if (profileAccess.payerContract !== 0) {
                    history.push(`${url}/insurance`);
                    if (isMobileLayout) {
                      toggleDrawer(false);
                    }
                  }
                }}
                className={`noselect
                  ${profileAccess.payerContract === 0 ? 'disabled' : ''}
                  ${pathname.endsWith('/insurance') ? 'activeitem' : ''}
                  ${css(styles.drawerMenuItem, pathname.endsWith('/insurance') &&
                  styles.drawerMenuItemActive)}`}
              >
                <Link
                  to={`${url}/insurance`}
                  onClick={() => {
                    if (profileAccess.payerContract !== 0) {
                      if (isMobileLayout) {
                        toggleDrawer(false);
                      }
                    }
                  }}
                  // className={css(styles.linkStyles, pathname.endsWith('/insurance') && styles.selectedLink)}
                  className={`${profileAccess.caseLedger === 0 ? 'disabled-link' : ''} ${css(styles.linkStyles, pathname.endsWith('/insurance') && styles.selectedLink)}`}
                >
                  {/* Icon */}
                  <div className={css(styles.drawerMenuItemIcon)}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="29" height="29" fill="inherit">
                      <path fill="inherit" fillRule="evenodd" d="M4 0a4 4 0 0 0-4 4v20.8a4 4 0 0 0 4 4h20.8a4 4 0 0 0 4-4V4a4 4 0 0 0-4-4H4Zm.202 5.829L14.488 2.4l10.286 3.429v10.285c0 5.681-4.605 10.286-10.286 10.286-5.681 0-10.286-4.604-10.286-10.286V5.83Zm18.644 10.283a8.357 8.357 0 0 1-16.715 0V7.273l8.357-2.946 8.358 2.946v8.84Zm-9.793-3.88c0 .633.487 1.04 2 1.433 1.514.393 3.133 1.04 3.133 2.933 0 1.367-1.033 2.12-2.333 2.367v1.433h-2v-1.446c-1.28-.274-2.373-1.094-2.453-2.554h1.466c.074.787.614 1.4 1.987 1.4 1.473 0 1.8-.733 1.8-1.193 0-.62-.333-1.207-2-1.607-1.86-.446-3.133-1.213-3.133-2.753 0-1.287 1.04-2.127 2.333-2.407v-1.44h2v1.46c1.394.34 2.094 1.394 2.14 2.54H16.52c-.04-.833-.48-1.4-1.667-1.4-1.127 0-1.8.507-1.8 1.234Z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className={css(styles.drawerMenuItemText)}>
                    Insurance
                  </div>
                </Link>
                {/* Access */}
                <div className='fx fx__v-center'>
                  {profileAccess.payerContract === 6 && readWriteIcon}
                  {profileAccess.payerContract === 4 && readOnlyIcon}
                  {profileAccess.payerContract === 0 && noAccessIcon}
                </div>
              </div>
            </div>
            {/* Entity Admin Privileges */}
            <div className={css(styles.drawerAccordian)}>
              <div className={css(styles.drawerFile)}>
                <div>
                  My Relationships
                </div>
              </div>
              {/* ---- Practice Affiliations ---- */}
              <div
                onClick={() => {
                  if (profileAccess.practiceAffiliations !== 0) {
                    history.push(`${url}/practices`);
                    if (isMobileLayout) {
                      toggleDrawer(false);
                    }
                  }
                }}
                className={`noselect
                  ${profileAccess.practiceAffiliations === 0 ? 'disabled' : ''}
                  ${pathname.endsWith('/practices') ? 'activeitem' : ''}
                  ${css(styles.drawerMenuItem, pathname.endsWith('/practices') &&
                  styles.drawerMenuItemActive)}`}
                style={{ cursor: profileAccess.practiceAffiliations === 0 ? 'not-allowed' : 'pointer' }}
              >
                <Link
                  to={`${url}/practices`}
                  onClick={() => {
                    if (profileAccess.practiceAffiliations !== 0) {
                      if (isMobileLayout) {
                        toggleDrawer(false);
                      }
                    }
                  }}
                  // className={css(styles.linkStyles, pathname.endsWith('/practices') && styles.selectedLink)}
                  className={`${profileAccess.practiceAffiliations === 0 ? 'disabled-link' : ''} ${css(styles.linkStyles, pathname.endsWith('/practices') && styles.selectedLink)}`}
                >
                  {/* Icon */}
                  <div className={css(styles.drawerMenuItemIcon)}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="22" height="24" fill="inherit">
                      <path fill="inherit" fillRule="evenodd" d="M17.998 10.127c.688.302 1.31.721 1.858 1.253a6.002 6.002 0 0 1 1.858 4.315V20.1c0 1.338-1.123 2.426-2.5 2.426h-6.818c-.39.887-1.297 1.475-2.299 1.475H2.5C1.12 24 0 22.91 0 21.574v-4.401c0-1.627.658-3.161 1.85-4.318a6.239 6.239 0 0 1 1.858-1.253 5.5 5.5 0 0 1-1.993-1.737A5.234 5.234 0 0 1 .76 6.848c0-2.963 2.483-5.373 5.536-5.373 1.552 0 3.009.623 4.055 1.715A5.39 5.39 0 0 1 12.209.99 5.615 5.615 0 0 1 15.41 0c3.053 0 5.536 2.41 5.536 5.373 0 1.085-.33 2.127-.955 3.02a5.468 5.468 0 0 1-1.993 1.734Zm-2.59-8.229c-.957 0-1.855.361-2.53 1.018a3.389 3.389 0 0 0-1.048 2.456c0 .93.372 1.801 1.049 2.456a3.598 3.598 0 0 0 2.53 1.018c.958 0 1.855-.361 2.53-1.018a3.4 3.4 0 0 0 0-4.912 3.598 3.598 0 0 0-2.53-1.018ZM2.715 6.848c0-.93.372-1.8 1.049-2.455a3.598 3.598 0 0 1 2.53-1.018c.958 0 1.855.36 2.53 1.018a3.398 3.398 0 0 1 0 4.911 3.598 3.598 0 0 1-2.53 1.018c-.955 0-1.856-.36-2.533-1.018A3.401 3.401 0 0 1 2.714 6.85Zm7.76 15.094a.502.502 0 0 0 .159-.37v-4.397a4.116 4.116 0 0 0-1.23-2.929l-.009-.01a4.372 4.372 0 0 0-3.1-1.275c-1.153 0-2.24.438-3.064 1.237a4.118 4.118 0 0 0-1.274 2.974v4.4c0 .29.242.524.54.524h7.597a.544.544 0 0 0 .38-.154Zm-.328-9.596a6.254 6.254 0 0 0-1.267-.745 5.475 5.475 0 0 0 2.472-2.568c.424.44.92.812 1.468 1.093a6.291 6.291 0 0 0-1.859 1.253c-.305.3-.578.622-.814.967Zm9.064 8.277c.297 0 .54-.236.54-.524v-4.404c0-1.12-.452-2.175-1.275-2.973a4.372 4.372 0 0 0-3.064-1.238 4.37 4.37 0 0 0-3.87 2.314 5.913 5.913 0 0 1 1.051 3.372v3.453h6.618Z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className={css(styles.drawerMenuItemText)}>
                    Practice Affiliations
                  </div>
                </Link>
                {/* Access */}
                <div className='fx fx__v-center'>
                  {profileAccess.practiceAffiliations === 6 && readWriteIcon}
                  {profileAccess.practiceAffiliations === 4 && readOnlyIcon}
                  {profileAccess.practiceAffiliations === 0 && noAccessIcon}
                </div>
              </div>
              {/* ------ Facility Privileges ------ */}
              <div
                onClick={() => {
                  if (profileAccess.facilityAffiliations !== 0) {
                    history.push(`${url}/facilities`);
                    if (isMobileLayout) {
                      toggleDrawer(false);
                    }
                  }
                }}
                className={`noselect
                  ${profileAccess.facilityAffiliations === 0 ? 'disabled' : ''}
                  ${pathname.endsWith('/facilities') ? 'activeitem' : ''}
                  ${css(styles.drawerMenuItem, pathname.endsWith('/facilities') &&
                  styles.drawerMenuItemActive)}`}
                style={{ cursor: profileAccess.facilityAffiliations === 0 ? 'not-allowed' : 'pointer' }}
              >
                <Link
                  to={`${url}/facilities`}
                  onClick={() => {
                    if (profileAccess.facilityAffiliations !== 0) {
                      if (isMobileLayout) {
                        toggleDrawer(false);
                      }
                    }
                  }}
                  // className={css(styles.linkStyles, pathname.endsWith('/facilities') && styles.selectedLink)}
                  className={`${profileAccess.facilityAffiliations === 0 ? 'disabled-link' : ''} ${css(styles.linkStyles, pathname.endsWith('/facilities') && styles.selectedLink)}`}
                >
                  {/* Icon */}
                  <div className={css(styles.drawerMenuItemIcon)}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="inherit">
                      <path fill="inherit" fillRule="evenodd" d="M0 1a1 1 0 0 1 1-1h13.852a1 1 0 0 1 1 1v22.995H0V1Zm4.533 11.998h6.79v9.998h-6.79v-9.998Zm0-2.753h1.542V7.27h3.708v2.98h1.542V2.822H9.783v2.963H6.075V2.818H4.533v7.427Zm11.826-4.79h6.637a1 1 0 0 1 1 1v17.542H16.36V5.455Zm1.092 2.18h2.182v2.18h-2.182v-2.18Zm5.454 0h-2.182v2.18h2.182v-2.18Zm-2.182 3.276h2.182v2.181h-2.182v-2.181Zm-1.09 0h-2.182v2.181h2.182v-2.181Zm-2.182 3.267h2.182v2.181h-2.182v-2.181Zm5.454 0h-2.182v2.181h2.182v-2.181Z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className={css(styles.drawerMenuItemText)}>
                    Facility Privileges
                  </div>
                </Link>
                {/* Access */}
                <div className='fx fx__v-center'>
                  {profileAccess.facilityAffiliations === 6 && readWriteIcon}
                  {profileAccess.facilityAffiliations === 4 && readOnlyIcon}
                  {profileAccess.facilityAffiliations === 0 && noAccessIcon}
                </div>
              </div>
              {/* ------ Company Affiliations ------ */}
              <div
                onClick={() => {
                  if (profileAccess.companyAffiliations !== 0) {
                    history.push(`${url}/companies`);
                    if (isMobileLayout) {
                      toggleDrawer(false);
                    }
                  }
                }}
                className={`noselect
                  ${profileAccess.companyAffiliations === 0 ? 'disabled' : ''}
                  ${pathname.endsWith('/companies') ? 'activeitem' : ''}
                  ${css(styles.drawerMenuItem, pathname.endsWith('/companies') &&
                  styles.drawerMenuItemActive)}`}
                style={{
                  display: 'none',
                  cursor: profileAccess.companyAffiliations === 0 ? 'not-allowed' : 'pointer'
                }}
              >
                <Link
                  to={`${url}/companies`}
                  onClick={() => {
                    if (profileAccess.companyAffiliations !== 0) {
                      if (isMobileLayout) {
                        toggleDrawer(false);
                      }
                    }
                  }}
                  // className={css(styles.linkStyles, pathname.endsWith('/companies') && styles.selectedLink)}
                  className={`${profileAccess.companyAffiliations === 0 ? 'disabled-link' : ''} ${css(styles.linkStyles, pathname.endsWith('/companies') && styles.selectedLink)}`}
                >
                  {/* Icon */}
                  <div className={css(styles.drawerMenuItemIcon)}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="22" fill="inherit">
                      <path fill="inherit" fillRule="evenodd" d="M12 4.8h12v16.8H0V0h12v4.8ZM2.4 19.2h2.4v-2.4H2.4v2.4Zm2.4-4.8H2.4V12h2.4v2.4ZM2.4 9.6h2.4V7.2H2.4v2.4Zm2.4-4.8H2.4V2.4h2.4v2.4Zm2.401 14.4h2.4v-2.4h-2.4v2.4Zm2.4-4.8h-2.4V12h2.4v2.4Zm-2.4-4.8h2.4V7.2h-2.4v2.4Zm2.4-4.8h-2.4V2.4h2.4v2.4Zm2.397 14.4h9.6v-12h-9.6v2.4h2.4V12h-2.4v2.4h2.4v2.4h-2.4v2.4Zm7.2-9.6h-2.4V12h2.4V9.6Zm-2.4 4.8h2.4v2.4h-2.4v-2.4Z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className={css(styles.drawerMenuItemText)}>
                    Company Affiliations
                  </div>
                </Link>
                {/* Access */}
                <div className='fx fx__v-center'>
                  {profileAccess.companyAffiliations === 6 && readWriteIcon}
                  {profileAccess.companyAffiliations === 4 && readOnlyIcon}
                  {profileAccess.companyAffiliations === 0 && noAccessIcon}
                </div>
              </div>
              {/* ------ Profile Access Manager ------ */}
              <div
                onClick={() => {
                  if (profileAccess.profileAccessManager !== 0) {
                    history.push(`${url}/profile-access-manager`);
                    if (isMobileLayout) {
                      toggleDrawer(false);
                    }
                  }
                }}
                className={`noselect
                  ${profileAccess.profileAccessManager === 0 ? 'disabled' : ''}
                  ${pathname.endsWith('/profile-access-manager') ? 'activeitem' : ''}
                  ${css(styles.drawerMenuItem, pathname.endsWith('/profile-access-manager') &&
                  styles.drawerMenuItemActive)}`}
                style={{ cursor: profileAccess.profileAccessManager === 0 ? 'not-allowed' : 'pointer' }}
              >
                <Link
                  to={`${url}/profile-access-manager`}
                  onClick={() => {
                    if (profileAccess.profileAccessManager !== 0) {
                      if (isMobileLayout) {
                        toggleDrawer(false);
                      }
                    }
                  }}
                  // className={css(styles.linkStyles, pathname.endsWith('/profile-access-manager') && styles.selectedLink)}
                  className={`${profileAccess.profileAccessManager === 0 ? 'disabled-link' : ''} ${css(styles.linkStyles, pathname.endsWith('/profile-access-manager') && styles.selectedLink)}`}
                >
                  {/* Icon */}
                  <div className={css(styles.drawerMenuItemIcon)}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="19" height="24" fill="inherit">
                      <path fill="inherit" fillRule="evenodd" d="M15.997 8h-1.143V5.714A5.716 5.716 0 0 0 9.141 0a5.716 5.716 0 0 0-5.713 5.714V8H2.285A2.292 2.292 0 0 0 0 10.286v11.428A2.292 2.292 0 0 0 2.285 24h13.712a2.292 2.292 0 0 0 2.285-2.286V10.286A2.292 2.292 0 0 0 15.997 8ZM9.142 18.286A2.292 2.292 0 0 1 6.857 16a2.292 2.292 0 0 1 2.285-2.286A2.292 2.292 0 0 1 11.428 16a2.292 2.292 0 0 1-2.286 2.286ZM5.712 5.764v2.24h6.856v-2.24c0-1.916-1.537-3.474-3.428-3.474-1.89 0-3.428 1.558-3.428 3.474Z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className={css(styles.drawerMenuItemText)}>
                    Profile Access Manager
                  </div>
                </Link>
                {/* Access */}
                <div className='fx fx__v-center'>
                  {profileAccess.profileAccessManager === 6 && readWriteIcon}
                  {profileAccess.profileAccessManager === 4 && readOnlyIcon}
                  {profileAccess.profileAccessManager === 0 && noAccessIcon}
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* Panel details */}
        <div
          className={css(styles.detail)}
          style={{
            width: isDrawerOpen ? `calc(100vw - ${drawerWidth}px)` : '100vw'
          }}
        >
          <div className={css(styles.detailHeader)}>
            <div
              onClick={() => toggleDrawer(!isDrawerOpen)}
            >
              <i
                className={`mr-2 fa ${isDrawerOpen ? 'fa-chevron-circle-left' : 'fa-chevron-circle-right'}`}
                style={{ fontSize: '24px' }}
              />
            </div>
            <div>{activeText}</div>
          </div>
          {/* Active component */}
          <div style={{ height: 'calc(100% - 40px)', padding: '0.5 rem' }}>
            <Switch>
              <Route exact path={path}>
                {profileAccess.profile > 0
                  ? <AboutUser
                    isProviderDashboard
                    showAppAlert={handelOnShowAppAlert}
                    userData={activeProvider}
                    reloadUserData={handleReloadUserData}
                    onRemove={(proc) => {
                      setActiveProvider((prvState) => {
                        return {
                          ...prvState,
                          surgicalProcedures: prvState.surgicalProcedures.filter(el => el.code !== proc.code)
                        };
                      });
                      removeSurgicalProcedure(proc);
                    }}
                    onFav={(proc) => {
                      setActiveProvider((prvState) => {
                        return {
                          ...prvState,
                          surgicalProcedures: prvState.surgicalProcedures.concat(proc)
                        };
                      });
                      addSurgicalProcedure(proc);
                    }}
                    showEllipsis={profileAccess.profile === 6}
                    profileAccess={profileAccess}
                  />
                  : <NoView />
                }
              </Route>
              <Route path={`${path}/profile`}>
                {profileAccess.profile > 0
                  ? <AboutUser
                    userData={activeProvider}
                    isProviderDashboard
                    showAppAlert={handelOnShowAppAlert}
                    reloadUserData={handleReloadUserData}
                    onRemove={(proc) => {
                      setActiveProvider((prvState) => {
                        return {
                          ...prvState,
                          surgicalProcedures: prvState.surgicalProcedures.filter(el => el.code !== proc.code)
                        };
                      });
                      removeSurgicalProcedure(proc);
                    }}
                    onFav={(proc) => {
                      setActiveProvider((prvState) => {
                        return {
                          ...prvState,
                          surgicalProcedures: prvState.surgicalProcedures.concat(proc)
                        };
                      });
                      addSurgicalProcedure(proc);
                    }}
                    showEllipsis={profileAccess.profile === 6}
                    profileAccess={profileAccess}
                  />
                  : <NoView />
                }
              </Route>
              <Route path={`${path}/scheduling`}>
                <Placeholder text='Placeholder for Scheduling' />
              </Route>
              <Route path={`${path}/revenue`}>
                {profileAccess.caseLedger > 0
                  ? <RevenueCycleManagement />
                  : <NoView />
                }
              </Route>
              <Route path={`${path}/credentialing`}>
                {profileAccess.credentialing > 0
                  ? <Placeholder text='Placeholder for Credentialing' />
                  : <NoView />
                }
              </Route>
              <Route path={`${path}/practices`}>
                {profileAccess.practiceAffiliations > 0
                  ? <ProviderPracticeGrid providerId={providerId} practices={activeProvider.practices} />
                  : <NoView />
                }
              </Route>
              <Route path={`${path}/facilities`}>
                {profileAccess.facilityAffiliations > 0
                  ? <ProviderFacilities />
                  : <NoView />
                }
              </Route>
              <Route path={`${path}/companies`}>
                {profileAccess.companyAffiliations > 0
                  ? <ProviderCompanies />
                  : <NoView />
                }
              </Route>
              <Route path={`${path}/payments`}>
                {profileAccess.paymentsLedger > 0
                  ? <h1>Payments Ledger placeholder</h1>
                  : <NoView />
                }
              </Route>
              <Route path={`${path}/insurance`}>
                {profileAccess.payerContract > 0
                  ? <h1>Insurance placeholder</h1>
                  : <NoView />
                }
              </Route>
              <Route path={`${path}/profile-access-manager`}>
                {profileAccess.profileAccessManager > 0
                  ? <ProfileAccessManager
                    providerId={providerId}
                    premissionMode={profileAccess.profileAccessManager}
                  />
                  : <NoView />
                }
              </Route>
            </Switch>
          </div>
        </div>
      </div>
    </div>
  );
};

function mapStateToProps({ user }) {
  return { ...user };
}

export default connect(mapStateToProps, {
  addAppAlert
})(ProviderDashboard);
